/**
 * The ids Heimdall files this page's Pulsar events under (utils/analytics/pulsar), carried on every FTUX event so the
 * owner's visit there joins their FTUX steps in ows-uxo (momentum-online ows_uxo/docs/EVENTS.md#heimdall-ids). The
 * beacon's own visitor and rolling visit are read from its cookies at each event; the owner-hub session is the one
 * pulsar-identity hands the beacon. Each is null when there is none or it is no UUID Heimdall takes, so an odd cookie
 * never costs the event a 400.
 */

/** The beacon's cookies: its visitor (400 days) and its visit (30 minutes with no event). */
const ANONYMOUS_USER_COOKIE = '_au';
const ANONYMOUS_SESSION_COOKIE = '_as';

/** A UUID as Heimdall takes one (heimdall/internal/edge/payload.go): versions 1 to 8, the RFC variant, any case. */
const HEIMDALL_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export type HeimdallIds = {
  sessionId: string | null;
  anonymousSessionId: string | null;
  anonymousUserId: string | null;
};

/** `value` lowercased, as Heimdall stores it, when it is a UUID Heimdall takes; else null. */
const heimdallUuid = (value: string | null | undefined): string | null =>
  value && HEIMDALL_UUID.test(value) ? value.toLowerCase() : null;

/** The first `name` cookie holding a UUID Heimdall takes, as the beacon reads its own (a shadowing copy may sit first). */
const cookieUuid = (name: string): string | null => {
  let cookies: string;
  try {
    cookies = document.cookie;
  } catch {
    // Cookies blocked: no id.
    return null;
  }
  for (const pair of cookies ? cookies.split('; ') : []) {
    const eq = pair.indexOf('=');
    if (eq <= 0 || pair.slice(0, eq) !== name) continue;
    try {
      const id = heimdallUuid(decodeURIComponent(pair.slice(eq + 1)));
      if (id) return id;
    } catch {
      // A malformed %-sequence: try the next copy.
    }
  }
  return null;
};

/** Heimdall's ids right now, with `sessionId` the owner-hub session (state.session.sessionId). */
export const heimdallIds = (sessionId: string | null | undefined): HeimdallIds => ({
  sessionId: heimdallUuid(sessionId),
  anonymousSessionId: cookieUuid(ANONYMOUS_SESSION_COOKIE),
  anonymousUserId: cookieUuid(ANONYMOUS_USER_COOKIE),
});
